import React, { useState, useEffect, useRef, useMemo } from 'react';
import { toast } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import { Modal, Button, Form } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import DataTable from 'react-data-table-component';
import { createCatalog, getAllCatalogs, updateCatalog, deleteCatalog } from '../commonApi';
import ActionCell from '../Utils/ActionCell';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { Loader } from '../Loader';

// Define the columns outside of the component
const getColumns = (handleEdit, handleDelete) => [
  { 
    name: "Catalog Name", 
    selector: row => row.catalogName || '', 
    sortable: true,
    sortField: 'catalogName',
    width: "450px" 
  },
  { 
    name: "Description", 
    selector: row => row.description || '', 
    sortable: true,
    sortField: 'description',
    width: "700px" 
  },
  {
    name: 'Actions',
    cell: row => (
      <ActionCell
        row={row}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    ),
  },
];

const Catalogs = () => {
  const [catalogs, setCatalogs] = useState([]);
  const [form, setForm] = useState({ catalogName: '', description: '' });
  const [editing, setEditing] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCreate, setShowCreate] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortField, setSortField] = useState('id');
  const [sortOrder, setSortOrder] = useState('desc');
  const [totalRows, setTotalRows] = useState(0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [isDeleteDisabled, setIsDeleteDisabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const catalogNameRef = useRef(null);
  const saveButtonRef = useRef(null);
  const expectedPageRef = useRef(1); 

  useEffect(() => {
    let isCancelled = false;

    const fetchCatalogs = async () => {
      setLoading(true);
      try {
        if (!isCancelled) {
          await loadCatalogs();
        }
      } catch (error) {
        if (!isCancelled) {
          toast.error('Failed to load catalogs');
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchCatalogs();
    return () => {
      isCancelled = true;
    };
  }, [page, rowsPerPage, search, sortField, sortOrder]);

  const loadCatalogs = async () => {
    setLoading(true);
    try {
      const response = await getAllCatalogs(page - 1, rowsPerPage, sortField, sortOrder, search);
      const catalogData = (response?.data?.data?.content || []).filter(
        (catalog) => catalog && typeof catalog === "object" && catalog.id
      );
      setCatalogs(catalogData);
      setTotalRows(response?.data?.data?.totalElements || 0);
    } catch (error) {
      toast.error('Failed to load catalogs');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    setErrors({ ...errors, [name]: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSaveDisabled || (saveButtonRef.current && saveButtonRef.current.disabled)) return;

    let formErrors = {};
    if (!form.catalogName.trim()) {
      formErrors.catalogName = 'Catalog name is required';
    }
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      if (formErrors.catalogName) {
        catalogNameRef.current.focus();
      }
      return;
    }

    if (saveButtonRef.current) {
      saveButtonRef.current.disabled = true;
    }
    setIsSaveDisabled(true);

    try {
      if (editing) {
        await updateCatalog(editing.id, form);
        toast.success('Catalog updated successfully');
      } else {
        await createCatalog(form);
        toast.success('Catalog created successfully');
      }
      setForm({ catalogName: '', description: '' });
      setEditing(null);
      setShowModal(false);
      setPage(1);
      expectedPageRef.current = 1;
      await loadCatalogs();
    } catch (error) {
      toast.error(error.response?.data?.error?.details || 'Failed to save catalog');
    } finally {
      if (saveButtonRef.current) {
        saveButtonRef.current.disabled = false;
      }
      setIsSaveDisabled(false);
    }
  };

  const handleEdit = (id) => {
    const deviceCatalog = catalogs.find(c => c.id === id);
    setForm({ catalogName: deviceCatalog.catalogName, description: deviceCatalog.description });
    setEditing(deviceCatalog);
    setShowCreate(false);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowConfirmModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditing(null);
    setForm({ catalogName: '', description: '' });
    setShowCreate(true);
  };

  const handleSearchInputChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
    expectedPageRef.current = 1;
  };

  const handlePerRowsChange = (newPerPage) => {
    setRowsPerPage(newPerPage);
    setPage(1);
    expectedPageRef.current = 1;
  };

  const handleSort = (column, sortDirection) => {
    setSortField(column.sortField);
    setSortOrder(sortDirection);
    setPage(1);
    expectedPageRef.current = 1;
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    expectedPageRef.current = newPage;
  };

  const confirmDelete = async () => {
    if (isDeleteDisabled) return;

    setIsDeleteDisabled(true);

    try {
      await deleteCatalog(deleteId);
      toast.success('Catalog deleted successfully');
      setPage(1);
      expectedPageRef.current = 1;
      await loadCatalogs();
    } catch (error) {
      toast.error(error.response?.data?.error?.details || 'Failed to delete catalog');
    } finally {
      setIsDeleteDisabled(false); // Reset state
      setShowConfirmModal(false); // Close modal
      setDeleteId(null); // Reset deleteId
    }
  };

  const columns = useMemo(() => getColumns(handleEdit, handleDelete), [handleEdit, handleDelete]);

  return (
    <div className="custom_tab_wrapper">
      {/* Inline CSS */}
      <style>
        {`
          .btn_red {
            background-color: #dc3545; /* Default red */
            border-color: #dc3545;
            color: white;
          }
          .btn_red:disabled {
            background-color: #f28b94; /* Light red for Deleting... */
            border-color: #f28b94;
            color: white;
            opacity: 0.8;
          }
          .btn_light__red {
            background-color: #f8d7da; /* Light red background for Cancel */
            border-color: #f5c6cb;
            color: #721c24;
          }
          .btn_light__red:disabled {
            background-color: #f8d7da; /* Same color when disabled */
            border-color: #f5c6cb;
            color: #721c24;
            opacity: 0.65; /* Bootstrap default opacity for disabled */
          }
        `}
      </style>

      <div className="tab-content" id="myTabContent">
        <div className="tab-pane fade show active" id="home" role="tabpanel" aria-labelledby="home-tab">
          <ul className="nav nav-tabs" id="myTab">
            <li className="nav-item">
              <button className="nav-link active" id="home-tab" data-bs-toggle="tab" data-bs-target="#home" type="button" role="tab" aria-controls="home" aria-selected="true">
                Catalogs: {totalRows}
              </button>
            </li>
          </ul>
          <div className="tab-content" id="myTabContent">
            <div className="tab-pane fade show active" id="home" role="tabpanel" aria-labelledby="home-tab">
              <div className="tab_content_wrapper full_height_tab">
                <div className="custom_table_action">
                  <div>
                    <input
                      className="form-control custom-search-input"
                      type="text"
                      onChange={handleSearchInputChange}
                      placeholder="Search by Catalog Name"
                      value={search}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <Button
                    variant="primary"
                    onClick={() => { setShowCreate(true); setShowModal(true); }}
                    style={{ backgroundColor: '#4340d2' }}
                    data-bs-toggle="tooltip"
                    data-bs-placement="left"
                    title="Add New Catalog"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </Button>
                </div>
                <div className="table-responsive h-100vh-150px">
                  {loading ? (
                    <div className="spin-loader">
                      <Loader />
                    </div>
                  ) : (
                    <div className="custom_table customs-table">
                      <DataTable
                        columns={columns}
                        data={catalogs}
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        paginationPerPage={rowsPerPage}
                        paginationDefaultPage={page}
                        onChangePage={handlePageChange}
                        onChangeRowsPerPage={handlePerRowsChange}
                        onSort={handleSort}
                        sortServer
                        highlightOnHover
                        fixedHeader
                        fixedHeaderScrollHeight="440px"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal for Create/Edit Catalog */}
      <Modal
        style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", marginTop: "90px", borderRadius: "5px", marginBottom: "20px" }}
        show={showModal}
        onHide={handleCloseModal}
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ color: "#4340d2" }}>{showCreate ? 'Create Catalog' : 'Edit Catalog'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group controlId="catalogName">
              <Form.Label>Catalog Name<span className="required-asterisk" style={{ color: "red" }}>*</span></Form.Label>
              <Form.Control
                type="text"
                name="catalogName"
                value={form.catalogName}
                onChange={handleChange}
                isInvalid={!!errors.catalogName}
                ref={catalogNameRef}
              />
              <Form.Control.Feedback type="invalid">{errors.catalogName}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group controlId="description">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={form.description}
                onChange={handleChange}
              />
            </Form.Group>
            <div className="d-flex justify-content-end" style={{ marginTop: "10px" }}>
              <button type="button" className="btn light_primary_btn me-3" onClick={handleCloseModal}>
                Cancel
              </button>
              <Button
                className="btn primary_btn"
                type="submit"
                ref={saveButtonRef}
                disabled={isSaveDisabled}
              >
                {isSaveDisabled ? "Saving..." : "Save"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Modal for Delete Confirmation */}
      {showConfirmModal && (
        <div
          className="modal fade discard_modal show"
          id="delete-model"
          style={{ display: 'block' }}
          aria-hidden={!showConfirmModal}
          aria-labelledby="exampleModalToggleLabel"
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <p className="modal-title" id="staticBackdropLabel">
                  Delete Catalog
                </p>
              </div>
              <div className="modal-body">
                <p className="modal-msg-title">Confirm Delete</p>
                <p className="modal-msg-desc">Are you sure you want to delete this catalog?</p>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn_light__red mx-2"
                  onClick={() => setShowConfirmModal(false)}
                  disabled={isDeleteDisabled}
                >
                  Cancel
                </button>
                <button
                  className="btn btn_red btn-loader"
                  onClick={confirmDelete}
                  disabled={isDeleteDisabled}
                >
                  {isDeleteDisabled ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Catalogs;